import { CriteriaOperator, CriteriaType } from '@@types/api.types';
import { IconButton } from '@components/core/icon-button.component';
import { Button } from '@components/ui/button';
import { withForm } from '@components/ui/form/form';
import { useGetCategories } from '@queries/category.query';
import { useGetDomains } from '@queries/domain.query';
import { useGetTrlFilterQuestions } from '@queries/trl-filter-question.query';
import { useGetUses } from '@queries/use.query';
import { useStore } from '@tanstack/react-form';
import {
  ANSWER_RESPONSE_PROGRESS_LABELS,
  CRITERIA_TYPE_OPTIONS,
  OPERATOR_OPTIONS_BY_TYPE,
  ORGANIZATION_TYPE_OPTIONS,
  TECHNOLOGY_TYPE_OPTIONS,
  cmmcLevelOptions,
  countries,
} from '@utils/constants';
import { mapToSelectOptions } from '@utils/select.util';
import { Trash2Icon } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import { challengeFormOptions } from '../challenge-form-options';

const COUNTRY_OPTIONS = countries.map((country) => ({
  label: country.name,
  value: country.code,
}));

const BOOLEAN_OPTIONS = [
  { label: 'True', value: 'true' },
  { label: 'False', value: 'false' },
];

const BOOLEAN_CRITERIA_TYPES = [CriteriaType.RESEARCH_VALIDATED, CriteriaType.SUBSYSTEMS];

/**
 * Criteria builder for phase-level criteria
 * Configures criteria specific to a phase
 */
export const CriteriaBuilder = withForm({
  ...challengeFormOptions,
  props: { position: undefined as unknown as number },
  render: function Render({ form, position }) {
    const { data: uses = [] } = useGetUses();
    const { data: domains = [] } = useGetDomains();
    const { data: categories = [] } = useGetCategories();
    const { data: trlQuestions = [] } = useGetTrlFilterQuestions();

    const usesOptions = useMemo(() => mapToSelectOptions(uses), [uses]);
    const domainsOptions = useMemo(() => mapToSelectOptions(domains), [domains]);
    const categoryOptions = useMemo(() => mapToSelectOptions(categories), [categories]);
    const trlQuestionOptions = useMemo(
      () => trlQuestions.map((question) => ({ label: question.prompt, value: question.id })),
      [trlQuestions],
    );

    const currentCriteria = useStore(form.store, (state) => state.values['challengePhases'][position].criteria);

    const subcategoryOptions = useMemo(() => {
      const categoryCriteria = (currentCriteria || []).filter((c) => c.type === CriteriaType.CATEGORY && c.values);

      const selectedCategoryIds = categoryCriteria.flatMap((c) => {
        const categoryValues = typeof c.values === 'string' ? [c.values] : (c.values as string[]);
        return categoryValues || [];
      });

      const subcategories = categories
        .filter((cat) => selectedCategoryIds.includes(cat.id))
        .flatMap((cat) => cat.subcategories || []);

      return mapToSelectOptions(subcategories);
    }, [currentCriteria, categories]);

    const SINGLE_VALUE_OPERATORS = [CriteriaOperator.IS, CriteriaOperator.IS_NOT];

    useEffect(() => {
      const validSubcategoryIds = new Set(subcategoryOptions.map((opt) => opt.value));

      currentCriteria?.forEach((criterion, index) => {
        if (criterion.type === CriteriaType.SUBCATEGORY && criterion.values) {
          const values = criterion.values;

          const fieldName = `challengePhases[${position}].criteria[${index}].values` as const;

          if (typeof values === 'string') {
            if (!validSubcategoryIds.has(values)) {
              form.setFieldValue(fieldName, null);
            }
          } else if (Array.isArray(values)) {
            const validValues = values.filter((id) => validSubcategoryIds.has(id));
            if (validValues.length !== values.length) {
              form.setFieldValue(fieldName, validValues.length > 0 ? validValues : null);
            }
          }
        }
      });
    }, [subcategoryOptions, currentCriteria, form, position]);

    return (
      <div className="space-y-4">
        <form.AppForm>
          <form.AppField name={`challengePhases[${position}].criteria`}>
            {(field) => (
              <div className="space-y-4">
                {field.state.value?.map((_, i) => {
                  const fieldName = `challengePhases[${position}].criteria[${i}]` as const;
                  return (
                    <div
                      key={i}
                      className="border rounded-lg p-4 space-y-4 bg-white"
                    >
                      <div className="flex items-start gap-2">
                        <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-2">
                          {/* Criteria Type Select */}
                          <form.AppField
                            name={`${fieldName}.type`}
                            listeners={{
                              onChange: ({ value }) => {
                                // For boolean types, automatically set operator to IS and default value to true
                                if (value && BOOLEAN_CRITERIA_TYPES.includes(value)) {
                                  form.setFieldValue(`${fieldName}.operator`, CriteriaOperator.IS);
                                  form.setFieldValue(`${fieldName}.values`, 'true');
                                } else {
                                  form.setFieldValue(`${fieldName}.operator`, null);
                                  form.setFieldValue(`${fieldName}.values`, null);
                                }
                              },
                            }}
                          >
                            {(typeField) => (
                              <typeField.SelectField
                                label="Criteria Type"
                                options={CRITERIA_TYPE_OPTIONS}
                                placeholder="Select type"
                                required
                              />
                            )}
                          </form.AppField>

                          <form.Subscribe
                            selector={(state) => state.values['challengePhases'][position].criteria[i].type}
                          >
                            {(type) => {
                              // Hide operator field for boolean types
                              if (type && BOOLEAN_CRITERIA_TYPES.includes(type)) {
                                return null;
                              }

                              return (
                                <form.AppField
                                  name={`${fieldName}.operator`}
                                  listeners={{
                                    onChange: () => {
                                      form.setFieldValue(`${fieldName}.values`, null);
                                    },
                                  }}
                                >
                                  {(operatorField) => (
                                    <operatorField.SelectField
                                      disabled={!type}
                                      label="Operator"
                                      options={type ? OPERATOR_OPTIONS_BY_TYPE[type] : []}
                                      placeholder="Select operator"
                                      required
                                    />
                                  )}
                                </form.AppField>
                              );
                            }}
                          </form.Subscribe>
                        </div>

                        <IconButton
                          className="mt-6"
                          ariaLabel="Remove criteria"
                          icon={<Trash2Icon size={16} />}
                          onClick={() => {
                            field.removeValue(i);
                          }}
                          size="lg"
                          variant="secondary"
                        />
                      </div>

                      <form.Subscribe
                        selector={(state) => ({
                          type: state.values['challengePhases'][position].criteria[i].type,
                          operator: state.values['challengePhases'][position].criteria[i].operator,
                        })}
                      >
                        {({ type, operator }) => {
                          if (!type || !operator) {
                            return null;
                          }

                          return (
                            <form.AppField name={`${fieldName}.values`}>
                              {(valuesField) => {
                                const trlMrlMaxScore = type === CriteriaType.TRL_SCORE ? 9 : 10;
                                const trlMrlLabel = type === CriteriaType.TRL_SCORE ? 'TRL' : 'MRL';

                                return (
                                  <>
                                    {type === CriteriaType.COUNTRY &&
                                      (SINGLE_VALUE_OPERATORS.includes(operator) ? (
                                        <valuesField.SelectField
                                          label="Value"
                                          options={COUNTRY_OPTIONS}
                                          placeholder="Select country"
                                          required
                                        />
                                      ) : (
                                        <valuesField.SelectMultipleField
                                          label="Values"
                                          options={COUNTRY_OPTIONS}
                                          placeholder="Select countries"
                                          required
                                        />
                                      ))}
                                    {type === CriteriaType.ORGANIZATION_TYPE &&
                                      (SINGLE_VALUE_OPERATORS.includes(operator) ? (
                                        <valuesField.SelectField
                                          label="Value"
                                          options={ORGANIZATION_TYPE_OPTIONS}
                                          placeholder="Select organization type"
                                          required
                                        />
                                      ) : (
                                        <valuesField.SelectMultipleField
                                          label="Values"
                                          options={ORGANIZATION_TYPE_OPTIONS}
                                          placeholder="Select organization types"
                                          required
                                        />
                                      ))}
                                    {type === CriteriaType.CMMC_LEVEL &&
                                      (SINGLE_VALUE_OPERATORS.includes(operator) ? (
                                        <valuesField.SelectField
                                          label="Value"
                                          options={cmmcLevelOptions}
                                          placeholder="Select CMMC level"
                                          required
                                        />
                                      ) : (
                                        <valuesField.SelectMultipleField
                                          label="Values"
                                          options={cmmcLevelOptions}
                                          placeholder="Select CMMC levels"
                                          required
                                        />
                                      ))}

                                    {type === CriteriaType.TYPE &&
                                      (SINGLE_VALUE_OPERATORS.includes(operator) ? (
                                        <valuesField.SelectField
                                          label="Value"
                                          options={TECHNOLOGY_TYPE_OPTIONS}
                                          placeholder="Select technology type"
                                          required
                                        />
                                      ) : (
                                        <valuesField.SelectMultipleField
                                          label="Values"
                                          options={TECHNOLOGY_TYPE_OPTIONS}
                                          placeholder="Select technology types"
                                          required
                                        />
                                      ))}
                                    {type === CriteriaType.USES && (
                                      <valuesField.SelectMultipleField
                                        label="Values"
                                        options={usesOptions}
                                        placeholder="Select uses"
                                        required
                                      />
                                    )}
                                    {type === CriteriaType.DOMAINS && (
                                      <valuesField.SelectMultipleField
                                        label="Values"
                                        options={domainsOptions}
                                        placeholder="Select domains"
                                        required
                                      />
                                    )}
                                    {type === CriteriaType.TRL_QUESTION && (
                                      <div className="grid grid-cols-1 gap-4">
                                        <form.AppField
                                          name={`${fieldName}.values.questionId`}
                                          listeners={{
                                            onChange: () => {
                                              form.setFieldValue(`${fieldName}.values.answers`, []);
                                            },
                                          }}
                                        >
                                          {(questionField) => (
                                            <questionField.SelectField
                                              label="TRL Question"
                                              options={trlQuestionOptions}
                                              placeholder="Select a TRL question"
                                              required
                                            />
                                          )}
                                        </form.AppField>
                                        <form.Subscribe
                                          selector={(state) =>
                                            state.values['challengePhases'][position].criteria[i].values
                                          }
                                        >
                                          {(criterionValues) => {
                                            const selectedQuestionId =
                                              criterionValues &&
                                              typeof criterionValues === 'object' &&
                                              !Array.isArray(criterionValues) &&
                                              'questionId' in criterionValues
                                                ? criterionValues.questionId
                                                : undefined;
                                            const selectedQuestion = trlQuestions.find(
                                              (question) => question.id === selectedQuestionId,
                                            );
                                            const answerOptions = (selectedQuestion?.answerOptions ?? []).map(
                                              (answer) => ({
                                                label: ANSWER_RESPONSE_PROGRESS_LABELS[answer],
                                                value: answer,
                                              }),
                                            );

                                            return (
                                              <form.AppField name={`${fieldName}.values.answers`}>
                                                {(answersField) => (
                                                  <answersField.SelectMultipleField
                                                    disabled={!selectedQuestionId}
                                                    label="Answers"
                                                    options={answerOptions}
                                                    placeholder={
                                                      selectedQuestionId ? 'Select answers' : 'Select a question first'
                                                    }
                                                    required
                                                  />
                                                )}
                                              </form.AppField>
                                            );
                                          }}
                                        </form.Subscribe>
                                      </div>
                                    )}
                                    {operator && type === CriteriaType.REVENUE_RANGE && (
                                      <>
                                        {operator === CriteriaOperator.BETWEEN ? (
                                          <div className="grid grid-cols-2 gap-4">
                                            <form.AppField name={`${fieldName}.values.min`}>
                                              {(field) => (
                                                <field.TextField
                                                  label="Min Revenue"
                                                  placeholder="1000000"
                                                  required
                                                  type="number"
                                                />
                                              )}
                                            </form.AppField>
                                            <form.AppField name={`${fieldName}.values.max`}>
                                              {(field) => (
                                                <field.TextField
                                                  label="Max Revenue"
                                                  placeholder="10000000"
                                                  required
                                                  type="number"
                                                />
                                              )}
                                            </form.AppField>
                                          </div>
                                        ) : (
                                          <form.AppField name={`${fieldName}.values.value`}>
                                            {(field) => (
                                              <field.TextField
                                                label="Revenue"
                                                placeholder="5000000"
                                                required
                                                type="number"
                                              />
                                            )}
                                          </form.AppField>
                                        )}
                                      </>
                                    )}
                                    {(type === CriteriaType.TRL_SCORE || type === CriteriaType.MRL_SCORE) && (
                                      <>
                                        {operator === CriteriaOperator.RANGE ? (
                                          <div className="grid grid-cols-2 gap-4">
                                            <form.AppField name={`${fieldName}.values.min`}>
                                              {(field) => (
                                                <field.TextField
                                                  label={`Min ${trlMrlLabel} Score`}
                                                  placeholder="1"
                                                  required
                                                  type="number"
                                                />
                                              )}
                                            </form.AppField>
                                            <form.AppField name={`${fieldName}.values.max`}>
                                              {(field) => (
                                                <field.TextField
                                                  label={`Max ${trlMrlLabel} Score`}
                                                  placeholder={String(trlMrlMaxScore)}
                                                  required
                                                  type="number"
                                                />
                                              )}
                                            </form.AppField>
                                          </div>
                                        ) : (
                                          <form.AppField name={`${fieldName}.values.value`}>
                                            {(field) => (
                                              <field.TextField
                                                label={`${trlMrlLabel} Score`}
                                                placeholder="5"
                                                required
                                                type="number"
                                              />
                                            )}
                                          </form.AppField>
                                        )}
                                      </>
                                    )}
                                    {type === CriteriaType.RESEARCH_VALIDATED && (
                                      <valuesField.SelectField
                                        label="Value"
                                        options={BOOLEAN_OPTIONS}
                                        placeholder="Select value"
                                        required
                                      />
                                    )}
                                    {type === CriteriaType.SUBSYSTEMS && (
                                      <valuesField.SelectField
                                        label="Value"
                                        options={BOOLEAN_OPTIONS}
                                        placeholder="Select value"
                                        required
                                      />
                                    )}
                                    {type === CriteriaType.CATEGORY &&
                                      (SINGLE_VALUE_OPERATORS.includes(operator) ? (
                                        <valuesField.SelectField
                                          label="Value"
                                          options={categoryOptions}
                                          placeholder="Select category"
                                          required
                                        />
                                      ) : (
                                        <valuesField.SelectMultipleField
                                          label="Values"
                                          options={categoryOptions}
                                          placeholder="Select categories"
                                          required
                                        />
                                      ))}
                                    {type === CriteriaType.SUBCATEGORY &&
                                      (SINGLE_VALUE_OPERATORS.includes(operator) ? (
                                        <valuesField.SelectField
                                          disabled={subcategoryOptions.length === 0}
                                          label="Value"
                                          options={subcategoryOptions}
                                          placeholder={
                                            subcategoryOptions.length === 0
                                              ? 'Add a criteria of a category first'
                                              : 'Select subcategory'
                                          }
                                          required
                                        />
                                      ) : (
                                        <valuesField.SelectMultipleField
                                          disabled={subcategoryOptions.length === 0}
                                          label="Values"
                                          options={subcategoryOptions}
                                          placeholder={
                                            subcategoryOptions.length === 0
                                              ? 'Add a criteria of a category first'
                                              : 'Select subcategories'
                                          }
                                          required
                                        />
                                      ))}
                                  </>
                                );
                              }}
                            </form.AppField>
                          );
                        }}
                      </form.Subscribe>
                    </div>
                  );
                })}

                <Button
                  className="h-auto"
                  onClick={() => field.pushValue({ values: null, operator: null, type: null })}
                  variant="link"
                >
                  + Add Criteria
                </Button>
              </div>
            )}
          </form.AppField>
        </form.AppForm>
      </div>
    );
  },
});
